import {
    BlockNoteEditor,
    defaultProps,
    insertOrUpdateBlock,
} from "@blocknote/core";
import { createReactBlockSpec } from "@blocknote/react";
import { redirect } from "next/navigation";
import {
    IconCircleFilled,
    IconExternalLink,
    IconHandClick,
    IconSettings,
    IconSettings2,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { rgbToHex } from "@/lib/utils";
import { ColorPicker } from "../content/ColorPicker";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";

export const ButtonBlock = createReactBlockSpec(
    {
        type: "buttonblock",
        propSchema: {
            textAlignment: defaultProps.textAlignment,
            url: {
                default: "https://",
            },
            backround: {
                default: "#252525",
            },
            text: {
                default: "#ffffff",
            },
        },
        content: "inline",
    },
    {
        render: ({ block, editor, contentRef }) => {
            const { url, backround, text } = block.props;

            return (
                <div
                    className={cn(
                        "flex items-center gap-2",
                        editor.isEditable && "w-full rounded-md bg-muted p-1"
                    )}
                >
                    <div className="flex items-center gap-2 rounded-md">
                        {editor.isEditable && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                        <IconSettings size={20} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="z-[99999]">
                                    <div className="px-2 py-1">
                                        <Label variant={"toggle"}>
                                            Goes to
                                        </Label>
                                        <Input
                                            variant={"card"}
                                            type="url"
                                            value={url}
                                            onChange={(e) =>
                                                editor.updateBlock(block, {
                                                    props: {
                                                        url: e.target.value,
                                                    },
                                                })
                                            }
                                            placeholder="https://"
                                        />
                                    </div>
                                    <DropdownMenuSeparator className="bg-accent" />
                                    <DropdownMenuLabel className="pb-1">
                                        Style
                                    </DropdownMenuLabel>
                                    <div className="space-y-2">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <div className="flex cursor-pointer items-center gap-3 px-2">
                                                    <IconCircleFilled
                                                        className="rounded-full border border-primary/50 hover:border-primary"
                                                        style={{
                                                            color: backround,
                                                        }}
                                                    />
                                                    <Label
                                                        variant={"toggle"}
                                                        className="cursor-pointer"
                                                    >
                                                        Background color
                                                    </Label>
                                                </div>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                className="z-[999999]"
                                                side="right"
                                            >
                                                <ColorPicker
                                                    value={backround}
                                                    onChange={(e) => {
                                                        editor.updateBlock(
                                                            block,
                                                            {
                                                                props: {
                                                                    backround:
                                                                        rgbToHex(
                                                                            e
                                                                        ),
                                                                },
                                                            }
                                                        );
                                                    }}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <div className="flex cursor-pointer items-center gap-3 px-2">
                                                    <IconCircleFilled
                                                        className="rounded-full border border-primary/50 hover:border-primary"
                                                        style={{
                                                            color: text,
                                                        }}
                                                    />
                                                    <Label
                                                        variant={"toggle"}
                                                        className="cursor-pointer"
                                                    >
                                                        Text color
                                                    </Label>
                                                </div>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                className="z-[999999]"
                                                side="right"
                                            >
                                                <ColorPicker
                                                    value={text}
                                                    onChange={(e) => {
                                                        editor.updateBlock(
                                                            block,
                                                            {
                                                                props: {
                                                                    text: rgbToHex(
                                                                        e
                                                                    ),
                                                                },
                                                            }
                                                        );
                                                    }}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                        {editor.isEditable ? (
                            <div
                                className={cn(
                                    `rounded-md bg-user-primary-color px-3 py-1 font-bold text-user-primary-text-color`
                                )}
                                style={{
                                    background: backround,
                                    color: text,
                                }}
                            >
                                <p
                                    ref={contentRef}
                                    className={cn(
                                        `min-w-[1ch] cursor-text whitespace-pre-wrap break-words outline-none `
                                    )}
                                    contentEditable={editor.isEditable}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.currentTarget.focus();
                                    }}
                                />
                            </div>
                        ) : (
                            <a href={url} target="_blank">
                                <div
                                    className={cn(
                                        `rounded-md bg-user-primary-color px-3 py-1 font-bold text-user-primary-text-color`
                                    )}
                                    style={{
                                        background: backround,
                                        color: text,
                                    }}
                                >
                                    <p
                                        ref={contentRef}
                                        className={cn(
                                            `min-w-[1ch] cursor-pointer whitespace-pre-wrap break-words outline-none`
                                        )}
                                        contentEditable={editor.isEditable}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.currentTarget.focus();
                                        }}
                                    />
                                </div>
                            </a>
                        )}
                    </div>
                </div>
            );
        },
    }
);

export const insertButtonBlock = () => ({
    title: "Button",
    group: "Advanced",
    onItemClick: (editor: BlockNoteEditor) => {
        insertOrUpdateBlock(editor, {
            // @ts-ignore
            type: "buttonblock", // Changed from "paragraph" to "buttonblock"
        });
    },
    aliases: ["btn", "button", "link"],
    icon: <IconHandClick />,
    subtext: "Customizable button with a URL",
});
